import { CheckIcon } from 'lucide-react';
import { FormattedMessage, useIntl } from 'react-intl';

import { ExternalLinkIcon } from '@/components/icons/external-link';
import { LockClosedIcon } from '@/components/icons/lock-closed';
import { Button } from '@/components/ui/button';
import { Tag } from '@/components/ui/tag';
import { Typography } from '@/components/ui/typography';
import { usePostHog } from '@/hooks/use-posthog';
import { SOURCE_PRODUCT_URL } from '@/utils/source-product';

interface SourceUpgradeCardProps {
  /** Sits left of the copy — pass a 32px icon tinted with the brand color. */
  icon: React.ReactNode;
  title: React.ReactNode;
  description: React.ReactNode;
  /** What the account gains by subscribing, one short phrase each. */
  benefits: string[];
  /** Replaces the default "Learn more" label on the product-page link. */
  ctaLabel?: React.ReactNode;
  /** Tracking for the product-page link. Defaults to the settings event. */
  onCtaClick?: () => void;
}

export function SourceUpgradeCard({
  icon,
  title,
  description,
  benefits,
  ctaLabel,
  onCtaClick,
}: SourceUpgradeCardProps) {
  const intl = useIntl();
  const posthog = usePostHog();

  return (
    <section className="shadow-selected border-fx-primary flex gap-4 rounded-lg border bg-white p-4">
      <div className="bg-fx-neutral-100 flex size-12 shrink-0 items-center justify-center rounded-lg">
        {icon}
      </div>

      <div className="flex flex-1 flex-col gap-4">
        <div className="flex flex-col gap-1">
          <div className="flex flex-wrap items-center gap-3">
            <Typography variant="medium2" as="h2">
              {title}
            </Typography>

            <Tag
              className="bg-fx-amber-50"
              icon={
                <LockClosedIcon className="text-fx-amber-800 size-3 shrink-0" />
              }
              text={intl.formatMessage({
                defaultMessage: 'SOURCE PREMIUM',
                description:
                  'Label on a badge marking a feature as available only to accounts that subscribe to the Source product',
              })}
              textClassName="!text-fx-amber-800 text-caption font-medium tracking-[0.8px] uppercase"
            />
          </div>

          <Typography variant="subtitle1">{description}</Typography>
        </div>

        <ul className="flex flex-wrap items-center gap-2">
          {benefits.map(benefit => (
            <li key={benefit}>
              <Tag
                className="bg-fx-neutral-100"
                icon={
                  <CheckIcon className="text-fx-neutral-600 size-3 shrink-0" />
                }
                text={benefit}
              />
            </li>
          ))}
        </ul>

        <Button asChild className="w-fit">
          <a
            href={SOURCE_PRODUCT_URL}
            target="_blank"
            rel="noreferrer"
            onClick={
              onCtaClick ??
              (() =>
                posthog.capture(
                  'source_settings:upgrade_to_source_button_click',
                ))
            }
          >
            {ctaLabel ?? (
              <FormattedMessage
                defaultMessage="Learn more"
                description="Button that opens the Source product page in a new tab, for accounts that want to subscribe and unlock a locked sourcing feature"
              />
            )}
            <ExternalLinkIcon />
          </a>
        </Button>
      </div>
    </section>
  );
}
